import React, { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import haxios from '../lib/haxios';
import * as Tooltip from '@radix-ui/react-tooltip';
import { GlobalContext } from '../context/GlobalContext';
import { RotateCw } from 'lucide-react';
import { CheckCircleIcon, XCircleIcon, WrenchScrewdriverIcon, BoltIcon } from '@heroicons/react/24/solid';
import { MdCarRepair } from 'react-icons/md'; // For MOT icon

const ServiceIcons = ({ mot, oil, ev }) => (
    <div className="flex space-x-2">
        <MdCarRepair className={`w-5 h-5 ${mot ? 'text-green-600' : 'text-gray-400'}`} title="MOT" />
        <WrenchScrewdriverIcon className={`w-5 h-5 ${oil ? 'text-green-600' : 'text-gray-400'}`} title="Oil Service" />
        <BoltIcon className={`w-5 h-5 ${ev ? 'text-green-600' : 'text-gray-400'}`} title="EV Service" />
    </div>
);


const CustomerSpendList = ({ selectedOperator }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const { uinfo } = useContext(GlobalContext);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const response = await haxios.get(`/visiondashboard/customer-spend?Rep=${selectedOperator.Inits}`);
            console.info('Customer spend data:', response.data);
            setCustomers(response.data);
            setLastUpdated(new Date());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
        const interval = setInterval(fetchCustomers, 30000);
        return () => clearInterval(interval);
    }, [selectedOperator.Inits]);

    if (loading && !customers.length) {
        return <div className="w-full p-4 text-center">Loading customer data...</div>;
    }

    if (!customers?.length) {
        return <div className="w-full p-4 text-center text-gray-500">All customers have sales for yesterday</div>;
    }

    return (
        <Tooltip.Provider>
            <div className="w-full bg-white rounded-lg shadow">
                <div className="flex justify-between items-center px-4 py-3">
                    <h2 className="font-bold">YESTERDAY - NON SPENDERS</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Last updated: {format(lastUpdated, 'HH:mm:ss')}</span>
                        <button
                            onClick={fetchCustomers}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            aria-label="Refresh customer data"
                            disabled={loading}
                        >
                            <RotateCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-9 gap-4 p-3 text-sm font-semibold bg-gray-100 rounded-t-lg">
                    <div className="col-span-2">Account</div>
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3">Last Invoice</div>
                    <div className="col-span-1 text-right">Services</div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                    {customers.map((customer) => (
                        <Tooltip.Root key={customer.KeyCode}>
                            <Tooltip.Trigger asChild>
                                <div className="grid grid-cols-9 gap-4 p-3 text-sm hover:bg-gray-50 border-b cursor-pointer items-center">
                                    <div className="col-span-2 truncate">{customer.KeyCode}</div>
                                    <div className="col-span-3 truncate">{customer.Name}</div>
                                    <div className="col-span-3 truncate">
                                        {customer.last_invoice ?
                                            `${customer.last_invoice} - £${Number(customer.last_invoice_value).toFixed(2)} (${format(new Date(customer.last_invoice_date), 'dd/MM/yy HH:mm')})` :
                                            'No previous invoices'
                                        }
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <ServiceIcons
                                            mot={customer.provides_mot}
                                            oil={customer.provides_oil_service}
                                            ev={customer.provides_ev_service}
                                        />
                                    </div>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Content
                                className="bg-white p-4 rounded-lg shadow-lg border z-50 w-80"
                                side="right"
                            >
                                <div className="space-y-2">
                                    <div><span className="font-semibold">Account:</span> {customer.KeyCode}</div>
                                    <div><span className="font-semibold">Customer:</span> {customer.Name}</div>
                                    <div><span className="font-semibold">Yesterday's Spend:</span> £{Number(customer.yesterday_spend).toFixed(2)}</div>
                                    {customer.last_invoice && (
                                        <>
                                            <div><span className="font-semibold">Last Invoice:</span> {customer.last_invoice}</div>
                                            <div><span className="font-semibold">Invoice Value:</span> £{Number(customer.last_invoice_value).toFixed(2)}</div>
                                            <div>
                                                <span className="font-semibold">Invoice Date:</span>{' '}
                                                {format(new Date(customer.last_invoice_date), 'dd/MM/yyyy HH:mm')}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <Tooltip.Arrow className="fill-white" />
                            </Tooltip.Content>
                        </Tooltip.Root>
                    ))}
                </div>
            </div>
        </Tooltip.Provider>
    );
};

export default CustomerSpendList;